import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";
import Auth from "../../auth/Auth";

const initialState = {
  data: [],
  status: "idle",
  error: null,
  imgData: "",
};

const { httpRequest } = Http();

// Helper function to handle HTTP requests
const createAuthThunk = (type, url, successHandler) =>
  createAsyncThunk(
    type,
    async ({ initialPost, navigate, navigatePath }, { rejectWithValue }) => {
      try {
        const response = await httpRequest(
          { method: "POST", url, body: initialPost },
          navigate
        );
        if (successHandler) successHandler.login(response);
        // console.log('response',response);
        return response;
      } catch (err) {
        return rejectWithValue(err.message || messages[type].error);
      }
    }
  );

const createSimpleThunk = (type, url, method = "POST", contentType) =>
  createAsyncThunk(
    type,
    async ({ initialPost, navigate }, { rejectWithValue }) => {
      try {
        const response = await httpRequest(
          { method, url, body: initialPost, contentType },
          navigate
        );
        return response;
      } catch (err) {
        return rejectWithValue(err.message || messages[type].error);
      }
    }
  );

const createDynamicThunk = (type, successHandler) =>
  createAsyncThunk(
    type,
    async ({ initialPost, url, navigate }, { rejectWithValue }) => {
      try {
        const response = await httpRequest(
          { method: "POST", url, body: initialPost },
          navigate
        );
        if (successHandler) successHandler.login(response);
        return response;
      } catch (err) {
        return rejectWithValue(err.message || messages[type].error);
      }
    }
  );

export const login = createAuthThunk("login", "admin/auth/login", Auth);
export const signUp = createAuthThunk("register", "admin/auth/register", Auth);
export const forgotPassword = createSimpleThunk(
  "forgotPassword",
  "admin/auth/password/forgot"
);
export const resetPassword = createSimpleThunk(
  "resetPassword",
  "admin/auth/password/reset"
);
export const verifyOTP = createDynamicThunk("verifyOTP", Auth);
export const resendOTP = createDynamicThunk("resendOTP");

// export const addImage = createSimpleThunk(
//   'uploadImage',
//   'storage',
//   'POST',
//   'multipart/form-data'
// )

// Async thunk to upload image
export const addImage = createAsyncThunk(
  "uploadImage",
  async ({ formData }, { rejectWithValue }) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: "storage",
        body: formData,
        contentType: "multipart/form-data",
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred");
    }
  }
);

export const logOut = createAsyncThunk(
  "logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: "admin/auth/logout",
        body: {},
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "An error occurred");
    }
  }
);

export const authorizeToken = createAsyncThunk(
  "authorizeToken",
  async ({ orgId, token }, { rejectWithValue }) => {
    try {
      const response = await httpRequest({
        method: "POST",
        url: "admin/auth/authorize", // Ensure this URL is correct
        body: { token, id: orgId },
      });

      // console.log("Response from server:", response); // Debugging log

      if (!response) {
        return rejectWithValue("No response received from the server");
      }

      if (response.error) {
        return rejectWithValue(response.error);
      }

      return response;
    } catch (err) {
      console.error("Error in authorizeToken:", err); // Debugging log
      return rejectWithValue(err?.message || "Token Authorization Failed");
    }
  }
);

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            login.pending,
            signUp.pending,
            forgotPassword.pending,
            verifyOTP.pending,
            resetPassword.pending,
            addImage.pending,
            resendOTP.pending,
            authorizeToken.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          state.error =
            action.payload || messages[action.type.split("/")[0]].error;
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [
            login.fulfilled,
            signUp.fulfilled,
            forgotPassword.fulfilled,
            verifyOTP.fulfilled,
            resetPassword.fulfilled,
            logOut.fulfilled,
            addImage.fulfilled,
            resendOTP.fulfilled,
            authorizeToken.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";
          if (action.type === "uploadImage/fulfilled") {
            state.imgData = action.payload;
          } else {
            state.data = action.payload;
          }
          action.meta.arg.setLoader(false);

          onboardingSlice.caseReducers.showToast(state, {
            payload: {
              success: true,
              message:
                action?.payload?.message ||
                messages[action.type.split("/")[0]].success,
              navigate: action.meta.arg.navigate,
              navigatePath: action.meta.arg.navigatePath,
              locationState: action.meta.arg.state,
            },
          });
          // }
        }
      )
      .addMatcher(
        (action) =>
          [
            login.rejected,
            signUp.rejected,
            forgotPassword.rejected,
            verifyOTP.rejected,
            resetPassword.rejected,
            addImage.rejected,
            resendOTP.rejected,
            authorizeToken.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload || messages[action.type.split("/")[0]].error;

          if (action.meta.arg) {
            action.meta.arg.setLoader(false);
          }
          if (action.type === "uploadImage/rejected") {
            state.imgData = {};
          } else {
            state.data = [];
          }
          onboardingSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export const { showToast } = onboardingSlice.actions;
export default onboardingSlice.reducer;